import * as React from 'react';

import { Button, Col, ConsoleDialog, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding } from 'lucide-react';

import * as govReportingState from '../../_state/govReporting.state';
import * as facilityState from '../../_state/govReportingFacility.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

export const FacilityAssignmentFormDialog = () => {
  const t = useTranslations();
  const [facilityId, setFacilityId] = React.useState<string | undefined>(
    undefined,
  );
  const [reportingFacilityId, setReportingFacilityId] = React.useState('');
  const [mlid, setMlid] = React.useState('');
  const editingId = useAtomValue(facilityState.$editingFacilityAssignment);
  const facilities = useAtomValue(facilityState.$govReportingFacilities);
  const configId = useAtomValue(govReportingState.$govReportingConfigId);
  const [isOpen, setIsOpen] = useAtom(
    facilityState.$isFacilityAssignmentDialogOpen,
  );
  const [{ isPending: isCreating }, createFacility] = useAtom(
    facilityState.$createGovReportingFacility,
  );
  const [{ isPending: isUpdating }, updateFacility] = useAtom(
    facilityState.$updateGovReportingFacility,
  );
  const setEditingId = useSetAtom(facilityState.$editingFacilityAssignment);
  const toast = useToast();

  const editingFacility = React.useMemo(
    () => facilities.data?.find((f) => f.id === editingId),
    [facilities.data, editingId],
  );
  const isEditing = !!editingFacility;
  const isPending = isCreating || isUpdating;

  React.useEffect(() => {
    if (isOpen && editingFacility) {
      setFacilityId(editingFacility.facilityId);
      setReportingFacilityId(editingFacility.reportingFacilityId ?? '');
      setMlid(editingFacility.mlid ?? '');
    } else if (isOpen) {
      setFacilityId(undefined);
      setReportingFacilityId('');
      setMlid('');
    }
  }, [isOpen, editingFacility]);

  const handleClose = () => {
    setIsOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingFacility) {
        await updateFacility({
          id: editingFacility.id,
          reportingFacilityId: reportingFacilityId || undefined,
          mlid: mlid || undefined,
        });
        toast.success(
          t.complianceView.govReporting.detail.facilityAssignment.dialog
            .messages.updateSuccess,
        );
      } else {
        if (!facilityId || !configId) return;
        await createFacility({
          governmentReportingConfigurationId: configId,
          facilityId,
          reportingFacilityId: reportingFacilityId || undefined,
          mlid: mlid || undefined,
        });
        toast.success(
          t.complianceView.govReporting.detail.facilityAssignment.dialog
            .messages.createSuccess,
        );
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isEditing) {
        toast.error(
          t.complianceView.govReporting.detail.facilityAssignment.dialog.messages.updateError(
            msg,
          ),
        );
      } else {
        toast.error(
          t.complianceView.govReporting.detail.facilityAssignment.dialog.messages.createError(
            msg,
          ),
        );
      }
    }
  };

  const canSubmit = isEditing ? true : !!facilityId && !!configId;

  return (
    <ConsoleDialog
      className="@FacilityAssignmentFormDialog"
      title={
        isEditing
          ? t.complianceView.govReporting.detail.facilityAssignment.dialog
              .editTitle
          : t.complianceView.govReporting.detail.facilityAssignment.dialog
              .createTitle
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={
        !!facilityId || reportingFacilityId !== '' || mlid !== ''
      }
      content={
        <Col gap6>
          <FormSection
            icon={LucideBuilding}
            title={
              isEditing
                ? t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .editTitle
                : t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .createTitle
            }
          >
            <Col gap4>
              {!isEditing && (
                <SelectFacility
                  label={
                    t.complianceView.govReporting.detail.facilityAssignment
                      .dialog.fields.facility
                  }
                  value={facilityId}
                  onChange={(value) => setFacilityId(value ?? undefined)}
                  placeholder={
                    t.complianceView.govReporting.detail.facilityAssignment
                      .dialog.placeholders.facility
                  }
                  isRequired
                />
              )}
              <TextField
                label={
                  t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .fields.reportingFacilityId
                }
                value={reportingFacilityId}
                onChange={setReportingFacilityId}
                placeholder={
                  t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .placeholders.reportingFacilityId
                }
              />
              <TextField
                label={
                  t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .fields.mlid
                }
                value={mlid}
                onChange={setMlid}
                placeholder={
                  t.complianceView.govReporting.detail.facilityAssignment.dialog
                    .placeholders.mlid
                }
              />
            </Col>
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleSubmit}
        >
          {isEditing
            ? t.complianceView.govReporting.detail.facilityAssignment.dialog
                .actions.save
            : t.complianceView.govReporting.detail.facilityAssignment.dialog
                .actions.create}
        </Button>
      }
    />
  );
};
